import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Snackbar,
    Alert,
    AlertTitle
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, VpnKey as VpnKeyIcon } from '@mui/icons-material';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const apiErrorMessage = (error) => error.response?.data?.detail || error.message;

const UsersPage = () => {
    const { currentUser: authUser } = useAuth();

    const [users, setUsers] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [userInput, setUserInput] = useState({
        username: '',
        email: '',
        password: '',
        role: 'user'
    });
    const [passwordChangeData, setPasswordChangeData] = useState({ userId: null, password: '' });
    const [deleteUserId, setDeleteUserId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [loading, setLoading] = useState(true);

    const fetchUsers = async () => {
        try {
            const response = await api.get('/api/users');
            setUsers(response.data);
        } catch (error) {
            showSnackbar('Error fetching users: ' + apiErrorMessage(error), 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    // Check if user has admin role
    if (authUser?.role !== 'admin') {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    <AlertTitle>Access Denied</AlertTitle>
                    You do not have permission to access the user administration page. Admin privileges required.
                </Alert>
            </Box>
        );
    }

    const handleOpenDialog = (user = null) => {
        if (user) {
            setEditingUser(user);
            setUserInput({
                username: user.username,
                email: user.email,
                password: '',
                role: user.role
            });
        } else {
            setEditingUser(null);
            setUserInput({
                username: '',
                email: '',
                password: '',
                role: 'user'
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingUser(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUserInput(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSaveUser = async () => {
        try {
            if (editingUser) {
                // Update existing user
                await api.put(`/api/users/${editingUser.id}`, {
                    username: userInput.username,
                    email: userInput.email,
                    role: userInput.role
                });
                showSnackbar('User updated successfully', 'success');
            } else {
                // Create new user
                await api.post('/api/users', {
                    username: userInput.username,
                    email: userInput.email,
                    password: userInput.password,
                    role: userInput.role
                });
                showSnackbar('User created successfully', 'success');
            }

            handleCloseDialog();
            fetchUsers(); // Refresh the user list
        } catch (error) {
            showSnackbar('Error saving user: ' + apiErrorMessage(error), 'error');
        }
    };

    const handleChangePassword = (userId) => {
        setPasswordChangeData({ userId, password: '' });
        setOpenPasswordDialog(true);
    };

    const handlePasswordChangeSubmit = async () => {
        if (!passwordChangeData.password.trim()) {
            showSnackbar('Password cannot be empty', 'error');
            return;
        }

        try {
            await api.put(`/api/users/${passwordChangeData.userId}/password`, {
                password: passwordChangeData.password
            });
            showSnackbar('Password updated successfully', 'success');
            setOpenPasswordDialog(false);
            setPasswordChangeData({ userId: null, password: '' });
        } catch (error) {
            showSnackbar('Error updating password: ' + apiErrorMessage(error), 'error');
        }
    };

    const handlePasswordChangeClose = () => {
        setOpenPasswordDialog(false);
        setPasswordChangeData({ userId: null, password: '' });
    };

    const handleDeleteUser = (userId) => {
        setDeleteUserId(userId);
        setOpenDeleteDialog(true);
    };

    const handleDeleteUserConfirm = async () => {
        try {
            await api.delete(`/api/users/${deleteUserId}`);
            showSnackbar('User deleted successfully', 'success');
            setOpenDeleteDialog(false);
            setDeleteUserId(null);
            fetchUsers(); // Refresh the user list
        } catch (error) {
            showSnackbar('Error deleting user: ' + apiErrorMessage(error), 'error');
        }
    };

    const handleDeleteUserCancel = () => {
        setOpenDeleteDialog(false);
        setDeleteUserId(null);
    };


    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                User Administration
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Add User
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Username</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Created At</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    Loading...
                                </TableCell>
                            </TableRow>
                        ) : users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No users found
                                </TableCell>
                            </TableRow>
                        ) : (
                            users.map((user) => (
                                <TableRow key={user.id}>
                                    <TableCell>{user.id}</TableCell>
                                    <TableCell>{user.username}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{user.role}</TableCell>
                                    <TableCell>{new Date(user.created_at).toLocaleString()}</TableCell>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenDialog(user)}
                                            color="primary"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleChangePassword(user.id)}
                                            color="secondary"
                                        >
                                            <VpnKeyIcon /> {/* Using VPN key icon for password change */}
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeleteUser(user.id)}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* User Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingUser ? 'Edit User' : 'Create New User'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="Username"
                            name="username"
                            value={userInput.username}
                            onChange={handleInputChange}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            label="Email"
                            name="email"
                            type="email"
                            value={userInput.email}
                            onChange={handleInputChange}
                            margin="normal"
                            required
                        />
                        {!editingUser && (
                            <TextField
                                fullWidth
                                label="Password"
                                name="password"
                                type="password"
                                value={userInput.password}
                                onChange={handleInputChange}
                                margin="normal"
                                required
                            />
                        )}
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Role</InputLabel>
                            <Select
                                name="role"
                                value={userInput.role}
                                onChange={handleInputChange}
                            >
                                <MenuItem value="user">User</MenuItem>
                                <MenuItem value="admin">Admin</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSaveUser} variant="contained">
                        {editingUser ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Password Change Dialog */}
            <Dialog open={openPasswordDialog} onClose={handlePasswordChangeClose} maxWidth="sm" fullWidth>
                <DialogTitle>Change Password</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="New Password"
                            name="password"
                            type="password"
                            value={passwordChangeData.password}
                            onChange={(e) => setPasswordChangeData({ ...passwordChangeData, password: e.target.value })}
                            margin="normal"
                            required
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePasswordChangeClose}>Cancel</Button>
                    <Button onClick={handlePasswordChangeSubmit} variant="contained">Change Password</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={handleDeleteUserCancel} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography>Are you sure you want to delete this user? This action cannot be undone.</Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteUserCancel}>Cancel</Button>
                    <Button onClick={handleDeleteUserConfirm} variant="contained" color="error">Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default UsersPage;